import React, { useState } from 'react';
import { X, Share2, Loader2, CheckCircle, AlertTriangle } from 'lucide-react';
import './ShareWorkspaceModal.css';

function ShareWorkspaceModal({ isOpen, onClose, selectedWorkspace, onShare }) {
  const [isSharing, setIsSharing] = useState(false);
  const [result, setResult] = useState(null);

  React.useEffect(() => {
    if (isOpen) {
      setIsSharing(false);
      setResult(null);
    }
  }, [isOpen]);

  const handleShare = async () => {
    if (!selectedWorkspace) return;

    setIsSharing(true);
    setResult(null);

    try {
      await onShare();
      setResult({ success: true });
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Share error:', error);
      setResult({
        success: false,
        error: error.message || 'Failed to share workspace.'
      });
    } finally {
      setIsSharing(false);
    }
  };

  if (!isOpen || !selectedWorkspace) return null;

  const isCurrentlyShared = selectedWorkspace.is_shared === true;

  return (
    <div className="share-modal-overlay" onClick={onClose}>
      <div className="share-modal" onClick={(e) => e.stopPropagation()}>
        <div className="share-modal-header">
          <div className="share-modal-title">
            <Share2 size={20} />
            <span>{isCurrentlyShared ? 'Unshare Workspace' : 'Share Workspace'}</span>
          </div>
          <button
            className="share-modal-close"
            onClick={onClose}
            disabled={isSharing}
          >
            <X size={18} />
          </button>
        </div>

        <div className="share-modal-content">
          {!result ? (
            <>
              <div className="share-section">
                <p className="share-description">
                  {isCurrentlyShared
                    ? 'This workspace is currently shared. Unsharing will revoke access for other organization members.'
                    : 'Share this workspace with your organization so other team members can collaborate on it.'}
                </p>
              </div>

              <div className="share-workspace-name">
                {selectedWorkspace.workspace_name}
              </div>

              {isSharing && (
                <div className="share-progress">
                  <div className="progress-bar">
                    <div className="progress-fill-indeterminate" />
                  </div>
                  <span className="share-progress-label">
                    {isCurrentlyShared ? 'Unsharing...' : 'Saving and sharing...'}
                  </span>
                </div>
              )}
            </>
          ) : (
            <div className="share-result">
              {result.success ? (
                <div className="result-success">
                  <CheckCircle size={40} className="result-icon success" />
                  <h3>{isCurrentlyShared ? 'Unshared' : 'Shared'}</h3>
                  <p>{isCurrentlyShared
                    ? 'Workspace is no longer shared.'
                    : 'Workspace is now shared with your organization.'}</p>
                </div>
              ) : (
                <div className="result-error">
                  <AlertTriangle size={40} className="result-icon error" />
                  <h3>Failed</h3>
                  <p>{result.error}</p>
                  <button
                    className="retry-button"
                    onClick={() => setResult(null)}
                  >
                    Try Again
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="share-modal-footer">
          {!result ? (
            <>
              <button
                className="share-cancel-button"
                onClick={onClose}
                disabled={isSharing}
              >
                Cancel
              </button>
              <button
                className={`share-confirm-button${isCurrentlyShared ? ' unshare' : ''}`}
                onClick={handleShare}
                disabled={isSharing}
              >
                {isSharing ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    {isCurrentlyShared ? 'Unsharing...' : 'Sharing...'}
                  </>
                ) : (
                  <>
                    <Share2 size={16} />
                    {isCurrentlyShared ? 'Unshare' : 'Share'}
                  </>
                )}
              </button>
            </>
          ) : result.success ? (
            <button className="share-close-button" onClick={onClose}>
              Close
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default ShareWorkspaceModal;
